export interface DetailImage {
  url: string;
  title: string;
  caption: string;
}

export interface Product {
  id: string;
  sku: string | null;
  name: string;
  description: string;
  price: string;
  sizes_json: Record<string, number>;
  images_json: string[];
  hero_image_url: string | null;
  detail_images_json: DetailImage[];
  story_image_url: string | null;
  is_active: boolean;
}

export interface CartItem {
  productId: string;
  productName: string;
  size: string;
  quantity: number;
  unitPrice: number;
  image: string;
}

export interface OrderTotals {
  subtotal: string | number;
  discount_total: string | number;
  shipping_fee: string | number;
  total: string | number;
}

export interface OrderQuote {
  subtotal: string;
  discount_total: string;
  shipping_fee: string;
  total: string;
  promo_code: string | null;
}

export interface PublicConfig {
  instapay_link: string;
  shipping_free_threshold: number;
  shipping_flat_fee: number;
}

export interface CreateOrderResponse {
  order_id: string;
  order_number: number;
  status: string;
  totals: OrderTotals;
  payment_method: string;
  items: {
    product_id: string;
    product_name: string;
    size: string;
    quantity: number;
    unit_price: string;
    line_total: string;
  }[];
}

export interface AdminToken {
  access_token: string;
  expires_in: number;
}

export interface PromoValidateResponse {
  valid: boolean;
  message: string;
}

export interface SubscriptionResponse {
  email: string;
  active: boolean;
}

export interface OkResponse {
  ok: boolean;
}

export interface OrderStatusUpdateResponse {
  ok: boolean;
  status: string;
}

export interface PaymentProofResponse {
  payment_proof_data: string;
}

export interface OrderOut {
  id: string;
  order_number: number;
  status: string;
  subtotal: string;
  discount_total: string;
  shipping_fee: string;
  total: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  payment_method: string;
  has_payment_proof: boolean;
  items: {
    product_id: string;
    product_name: string;
    size: string;
    quantity: number;
    unit_price: string;
    line_total: string;
  }[];
}

export interface PromoOut {
  id: number;
  code: string;
  discount_type: string;
  discount_value: string;
  scope_type: string;
  product_ids: string[];
  starts_at: string | null;
  ends_at: string | null;
  max_total_uses: number | null;
  max_uses_per_email: number | null;
  is_active: boolean;
  created_at: string;
}

export const SIZE_ORDER = ["S", "M", "L", "XL"];

export const formatPrice = (price: string | number): string => {
  const num = typeof price === "string" ? parseFloat(price) : price;
  if (isNaN(num)) return "EGP 0.00";
  return `EGP ${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const sortedSizes = (sizes_json: Record<string, number>) =>
  Object.entries(sizes_json).sort(([a], [b]) => {
    const ai = SIZE_ORDER.indexOf(a.toUpperCase());
    const bi = SIZE_ORDER.indexOf(b.toUpperCase());
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

export const totalStock = (sizes_json: Record<string, number>): number =>
  Object.values(sizes_json).reduce((s, q) => s + q, 0);
